import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Clock, Send, CreditCard, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REASONS = [
  { id: 'too_expensive', label: 'Pricing is too high for our budget' },
  { id: 'missing_features', label: 'Missing features we need' },
  { id: 'not_enough_time', label: 'Did not have enough time to evaluate' },
  { id: 'found_alternative', label: 'Found an alternative solution' },
  { id: 'not_right_fit', label: 'Not the right fit for our organization' },
  { id: 'technical_issues', label: 'Experienced technical issues' },
  { id: 'other', label: 'Other reason' },
];

const MISSING_FEATURES = [
  { id: 'integrations', label: 'More integrations (Slack, Teams, etc.)' },
  { id: 'analytics', label: 'Advanced analytics & reporting' },
  { id: 'customization', label: 'More customization options' },
  { id: 'languages', label: 'Multi-language support' },
  { id: 'sso', label: 'SSO / Enterprise authentication' },
  { id: 'api', label: 'API access' },
];

export function ExpiredTrialSurvey() {
  const { user, tenant, token, hasRole } = useAuth();
  const navigate = useNavigate();
  const isAdmin = hasRole(['admin']);
  
  const [reason, setReason] = useState('');
  const [missingFeatures, setMissingFeatures] = useState([]);
  const [wouldRecommend, setWouldRecommend] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleFeatureToggle = (featureId) => {
    setMissingFeatures(prev => 
      prev.includes(featureId) 
        ? prev.filter(f => f !== featureId)
        : [...prev, featureId]
    );
  };

  const handleSubmit = async () => {
    if (!reason) {
      toast.error('Please select a reason for not upgrading');
      return;
    }
    if (wouldRecommend === null) {
      toast.error('Please indicate if you would recommend Eva');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/feedback/trial-survey`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          reason,
          missing_features: missingFeatures,
          would_recommend: wouldRecommend,
          feedback,
          user_email: user?.email,
          tenant_id: tenant?.tenant_id,
          tenant_name: tenant?.display_name,
        }),
      });

      if (response.ok) {
        setIsSubmitted(true);
        toast.success('Thank you for your feedback!');
      } else {
        throw new Error('Failed to submit survey');
      }
    } catch (error) {
      console.error('Survey submission error:', error);
      toast.error('Failed to submit survey. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show thank you screen after submission
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg bg-slate-800/50 border-slate-700 text-white">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
              <Send className="h-8 w-8 text-green-400" />
            </div>
            <CardTitle className="text-2xl">Thank You!</CardTitle>
            <CardDescription className="text-slate-300">
              Your feedback helps us improve Eva for everyone.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isAdmin ? (
              <>
                <p className="text-center text-slate-300 text-sm">
                  Ready to continue? Upgrade now to unlock all features.
                </p>
                <Button 
                  onClick={() => navigate('/settings/billing')} 
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  View Plans & Upgrade
                </Button>
              </>
            ) : (
              <p className="text-center text-slate-300 text-sm">
                Please contact your administrator to upgrade your organization's plan.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl bg-slate-800/50 border-slate-700 text-white">
        <CardHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <Clock className="h-6 w-6 text-red-400" />
            </div>
            <div>
              <CardTitle className="text-xl">Your Trial Has Ended</CardTitle>
              <CardDescription className="text-slate-300">
                {tenant?.display_name || 'Your organization'}'s free trial has expired
              </CardDescription>
            </div>
          </div>
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 mt-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-200">
                Please complete this brief survey to help us understand your experience. 
                This is required before you can continue.
              </p>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Reason for not upgrading */}
          <div className="space-y-3">
            <Label className="text-base font-medium">
              Why didn't you upgrade? <span className="text-red-400">*</span>
            </Label>
            <RadioGroup value={reason} onValueChange={setReason} className="space-y-2">
              {REASONS.map((r) => (
                <div key={r.id} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-slate-700/50 transition-colors">
                  <RadioGroupItem value={r.id} id={r.id} className="border-slate-500" />
                  <Label htmlFor={r.id} className="cursor-pointer text-slate-200 font-normal">
                    {r.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Missing features (show if relevant reason selected) */}
          {(reason === 'missing_features' || reason === 'not_right_fit') && (
            <div className="space-y-3">
              <Label className="text-base font-medium">
                Which features were you missing?
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {MISSING_FEATURES.map((f) => (
                  <div 
                    key={f.id} 
                    className="flex items-center space-x-3 p-2 rounded-lg hover:bg-slate-700/50 transition-colors"
                  >
                    <Checkbox 
                      id={f.id} 
                      checked={missingFeatures.includes(f.id)}
                      onCheckedChange={() => handleFeatureToggle(f.id)}
                      className="border-slate-500"
                    />
                    <Label htmlFor={f.id} className="cursor-pointer text-slate-200 font-normal text-sm">
                      {f.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Would recommend */}
          <div className="space-y-3">
            <Label className="text-base font-medium">
              Would you recommend Eva to others? <span className="text-red-400">*</span>
            </Label>
            <RadioGroup 
              value={wouldRecommend === null ? '' : wouldRecommend.toString()} 
              onValueChange={(v) => setWouldRecommend(v === 'true')} 
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="true" id="recommend-yes" className="border-slate-500" />
                <Label htmlFor="recommend-yes" className="cursor-pointer text-slate-200">Yes</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="false" id="recommend-no" className="border-slate-500" />
                <Label htmlFor="recommend-no" className="cursor-pointer text-slate-200">No</Label>
              </div>
            </RadioGroup>
          </div>

          {/* Additional feedback */}
          <div className="space-y-3">
            <Label className="text-base font-medium">
              Any additional feedback? (optional)
            </Label>
            <Textarea 
              placeholder="Tell us what would have made you upgrade, or any other thoughts..."
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400 min-h-[100px]"
            />
          </div>

          {/* Submit */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button 
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
            </Button>
            {isAdmin && (
              <Button 
                variant="outline"
                onClick={() => navigate('/settings/billing')}
                className="flex-1 border-slate-600 text-slate-200 hover:bg-slate-700"
              >
                <CreditCard className="h-4 w-4 mr-2" />
                Skip & View Plans
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
